import { Button } from 'ds/components/button';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import React, { useEffect, useState } from 'react';
import * as Styled from './styled';
import PhoneIcon from 'assets/svg/phone-1-5.svg';
import LeadingIcon from 'assets/svg/circle-wavy-check.svg';
import { useLocale } from 'locale';
import { SmallText } from 'ds/components/typography';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { connect } from 'react-redux';
import { Social, TOOLTIP_DELAY } from 'screens/AgentOfficePages/common/components/Social';
import { CopyTooltip } from 'screens/AgentOfficePages/common/components/Social/styled';
import { routeSelector } from 'store/state/selectors/router';
import { Route, State as RouteState } from 'config/routes';
import { Card } from 'screens/AgentOfficePages/common/components/AgentCard';
import { State } from 'store/state';
import { PhoneButton } from 'screens/MortgageOfficePage/sections/Header/desktop/styled';
import { NoImg } from 'screens/AgentOfficePages/common/components/NoImg';
import { PageDetails } from 'screens/AgentOfficePages/types';
import { noop } from 'lodash';


export interface InfoProps {
  title: string;
  about: string;
  phone: string;
  logo: string;
  setContactOpen?: (open: boolean) => void;
  route: RouteState;
  pageDetails?: PageDetails;
  onPhoneClickEventHandler?: () => void;
  isLeadingAgent?: boolean;
}

const InfoBase: React.FC<InfoProps> = React.memo(({ title, about, isLeadingAgent, phone, logo, setContactOpen, route, pageDetails, onPhoneClickEventHandler = noop }) => {
  const { t } = useLocale();
  const [ copied, setCopied ] = useState<boolean | null>(null);
  const isMobile = useScreenBreakpoint() === 1;
  const isAgent = route.name === Route.AgentPage;

  const handleOnCopy = React.useCallback(() => {
    if (!copied) setCopied(true);
    return false;
  }, [ copied, setCopied ]);

  const renderTooltip = () => copied ? (
    <CopyTooltip>
      <SmallText>{t('content.copied')}</SmallText>
    </CopyTooltip>
  ) : null;

  const disableClick = React.useCallback((e: React.SyntheticEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (copied) {
      timer = setTimeout(() => setCopied(null), TOOLTIP_DELAY);
    }
    return () => clearTimeout(timer);
  }, [ copied, setCopied ]);

  return (
    <Styled.InfoWrapper>
      <Styled.InfoContent>
        <Styled.InfoSection>
          {isAgent ? <Card iconUrl={logo} isLeading={isLeadingAgent} fullSizeOn size="agent" /> : <Styled.Logo>{logo ? <img src={logo} /> : <NoImg size={48} />}</Styled.Logo>}
          <Styled.Content>
            <Styled.MainTitle style={isLeadingAgent ? { display: 'flex', alignItems: 'center', gap: '4px' } : {}} withWrap={isMobile}>
              {title}
              {isLeadingAgent ? <LeadingIcon /> : null}
            </Styled.MainTitle>
            <Styled.AddressLine>{about}</Styled.AddressLine>
            {isMobile && isAgent && phone ? (
              <PhoneButton onClick={onPhoneClickEventHandler} href={createPhoneURI(phone)}>
                {phone}
                <PhoneIcon />
              </PhoneButton>
            ) : null}
            {isMobile ? null : (
              <Styled.ContactSection>
                {phone ? (
                  <Styled.PhoneButton
                    data-auto="phone-number-button"
                    size="large"
                    onClick={onPhoneClickEventHandler}
                    href={createPhoneURI(phone)}
                  >
                    {phone}
                    <CopyToClipboard text={phone} onCopy={handleOnCopy}>
                      <Styled.PhoneIconWrapper onClick={disableClick}>
                        <PhoneIcon/>
                        {renderTooltip()}
                      </Styled.PhoneIconWrapper>
                    </CopyToClipboard>
                  </Styled.PhoneButton>
                ) : null}
                <Button mode="ghost" size="large" onClick={() => setContactOpen(true)}>{t('developerPage.contactButton', { submitted: false, isMobile: false })}</Button>
              </Styled.ContactSection>
            )}
          </Styled.Content>
        </Styled.InfoSection>
        {isMobile ? null : <Styled.SocialWrapper><Social name={title} source="header" pageDetails={pageDetails} /></Styled.SocialWrapper>}
      </Styled.InfoContent>
    </Styled.InfoWrapper>
  );
});

const mapStateToProps = (state: State) => ({
  route: routeSelector(state),
});

export const Info = connect(mapStateToProps)(InfoBase);
